// New engagement page — mirrors the legacy NewEngagementPage component.
// Two-step flow: pick pentest type, then fill engagement details + targets.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { PageHeader } from "@/components/ui/PageHeader";
import { useCreateEngagement } from "@/hooks/useCreateEngagement";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import { ApiError } from "@/lib/api";

const PENTEST_TYPE_IDS = [
  { id: "web_application", icon: "🌐" },
  { id: "api", icon: "⚡" },
  { id: "network", icon: "🔌" },
  { id: "cloud", icon: "☁️" },
  { id: "mobile", icon: "📱" },
  { id: "iot", icon: "🔧" },
  { id: "ai", icon: "🤖" },
] as const;

export function NewEngagementPage() {
  const navigate = useNavigate();
  const t = useDict().app.newEngagement;
  const [step, setStep] = useState<1 | 2>(1);
  const [pentestType, setPentestType] = useState("web_application");
  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [scope, setScope] = useState("");
  const [targetsRaw, setTargetsRaw] = useState("");

  const create = useCreateEngagement();

  const typeLabel = t.types[pentestType as keyof typeof t.types]?.label ?? t.unknown;

  async function handleCreate() {
    if (!name.trim() || !targetsRaw.trim()) return;
    const targets = targetsRaw
      .split("\n")
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      const engagement = await create.mutateAsync({
        name: name.trim(),
        client_name: clientName.trim(),
        pentest_type: pentestType,
        scope_description: scope.trim(),
        targets,
      });
      navigate(`/app/engagements/${engagement.id}`);
    } catch {
      // Error is exposed via `create.error` below
    }
  }

  // ─── Step 1: pentest type selection ───────────────────────────────────
  if (step === 1) {
    return (
      <div>
        <PageHeader
          title={t.pickTitle}
          subtitle={t.pickSubtitle}
          breadcrumbs={[t.bcEngagements, t.bcNew]}
        />
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          }}
        >
          {PENTEST_TYPE_IDS.map((p) => {
            const active = pentestType === p.id;
            return (
              <div
                key={p.id}
                onClick={() => {
                  setPentestType(p.id);
                  setStep(2);
                }}
                className="cb-grow rounded p-4 cursor-pointer transition-all duration-150"
                style={{
                  background: active ? "rgb(var(--color-accent-bg))" : "rgb(var(--color-card))",
                  border: `1px solid ${active ? "rgb(var(--color-accent))" : "rgb(var(--color-stroke))"}`,
                }}
              >
                <div className="text-[32px] mb-2">{p.icon}</div>
                <div
                  className="font-display text-[16px] font-semibold"
                  style={{ color: active ? "rgb(var(--color-accent))" : "rgb(var(--color-content))" }}
                >
                  {t.types[p.id].label}
                </div>
                <div className="font-mono text-[10px] text-muted mt-[6px] leading-[1.5]">
                  {t.types[p.id].description}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Step 2: engagement details ───────────────────────────────────────
  const errorMsg =
    create.error instanceof ApiError
      ? String(create.error.detail)
      : create.error instanceof Error
        ? create.error.message
        : null;

  return (
    <div>
      <PageHeader
        title={fmt(t.detailsTitle, { label: typeLabel })}
        subtitle={t.detailsSubtitle}
        breadcrumbs={[t.bcEngagements, t.bcNew, typeLabel]}
        actions={
          <Button variant="ghost" onClick={() => setStep(1)}>
            {t.changeType}
          </Button>
        }
      />
      <Card>
        <Input
          label={t.nameLabel}
          value={name}
          onChange={setName}
          placeholder={t.namePlaceholder}
        />
        <Input
          label={t.clientLabel}
          value={clientName}
          onChange={setClientName}
          placeholder={t.clientPlaceholder}
        />
        <Input
          label={t.scopeLabel}
          value={scope}
          onChange={setScope}
          placeholder={t.scopePlaceholder}
          textarea
        />
        <Input
          label={t.targetsLabel}
          value={targetsRaw}
          onChange={setTargetsRaw}
          placeholder={"example.com\nhttps://api.example.com\n10.0.1.50"}
          textarea
        />
        {errorMsg && (
          <div className="text-danger font-mono text-[12px] mb-3">
            {t.error} {errorMsg}
          </div>
        )}
        <Button
          variant="primary"
          onClick={handleCreate}
          disabled={!name.trim() || !targetsRaw.trim() || create.isPending}
        >
          {create.isPending ? t.creating : t.createContinue}
        </Button>
      </Card>
    </div>
  );
}
